import { computed, toRefs } from 'vue';
import router from '@/router';
import { defineStore } from 'pinia';
import i18n from '@/plugins/vue-i18n';
import {
  addOrgUserRole,
  createOrgClaim,
  createOrgOrder,
  deleteDocuments,
  deleteOrgLogo,
  deleteUser,
  removeOrgUserRole,
  getAccountsPayable,
  getAccountsPayableTotals,
  getBoqTemplates,
  getCurrentOrgUsers,
  getCurrentUserOrgs,
  getOrderVendors,
  getOrganisationDetails,
  getOrganisationProjects,
  getOrgProjectListByUser,
  getOrgSites,
  getPaymentRequests,
  getPaymentRequestsCount,
  getPaymentRequestSites,
  getPaymentRequestTotals,
  getPaymentRequestVendors,
  getProjectManagers,
  registerOrganisation,
  setOrganisationPrimary,
  updateOrganisationDetails,
  updateTemplate,
  updateUserRole,
  uploadDocuments,
  uploadOrgLogo,
} from '@/api/organisations.service';
import {
  AccountPayable,
  AccountsParams,
  CreateOrgBody,
  CreateOrgOrder,
  DeleteDocsBody,
  OrderVendor,
  Organisation,
  OrganisationProjectListForUser,
  OrgDocsBody,
  OrgSite,
  OrgSiteParams,
  OrgUser,
  PaymentRequest,
  PaymentRequestTotals,
  PRItem,
  ProjectParams,
  UserRole,
} from '@/types/api/organisations.types';
import { Template } from '@/types/api/boqs.types';
import { OrgRoles } from '@/types/api/roles.types';
import { BOQStatus } from '@/types/api/sites.types';
import { Project } from '@/types/api/project.types';
import { Pagination } from '@/types/api/response.types';
import { User, UserOrganisation } from '@/types/api/auth.types';
import { SelectedRequest } from '@/types/api/payment-request.types';

import { useBoqStore } from '@/stores/boqs.store';
import { useAuthStore } from '@/stores/auth.store';
import { useOrdersStore } from '@/stores/orders.store';
import { useClaimsStore } from '@/stores/claims.store';
import { useToastStore } from '@/stores/utils/toast.store';
import { useTotalsStore } from '@/stores/utils/totals.store';

import { sortByStringField } from '@/common/utils/object.utils';
import { documentUploadToast } from '@/common/utils/general.utils';
import { getRoleDisplayName, selectedOrgId, sortRolesByOrder } from '@/common/utils/role.utils';
import {
  showRoleAddedToast,
  showRoleRemovedToast,
  showUserRemovedToast,
} from '@/common/utils/user-role-toast.utils';

import { analyticsService } from '@/plugins/post-hog';

const { t } = i18n.global;

const currentOrgId = () => useAuthStore().loggedInUser.selectedOrg!.id;
const currentOrgName = () => useAuthStore().loggedInUser.selectedOrg!.name;

// Context: a refresh after an action reads the shared payment request list and its pushed-back
// count while the viewer may start their own read (a filter, a page, another vendor), so only the
// latest read started writes; an earlier one landing after it would put the old filter on screen.
let paymentRequestsRead = 0;
let pushedBackCountRead = 0;
// The log payment drawer fills two caches from one action, so each keeps its own latest read: a
// context options read must not drop an Approved read still in flight, or the other way round.
let logPaymentRequestsRead = 0;
let logPaymentContextOptionsRead = 0;

export const useOrgStore = defineStore('organisations', {
  state: () => ({
    currentOrgDetails: {} as Organisation,
    currentOrgUsers: [] as OrgUser[],
    // Bumped by every org-user role mutation. getCurrentOrgUsers captures it
    // before its request and discards the response if a mutation landed while
    // the fetch was in flight — prevents a stale list snapshot from clobbering a
    // fresher mutation result (last-write-wins race on slow networks / back-nav).
    orgUsersVersion: 0,
    currentUserOrgs: [] as UserOrganisation[],
    allProjects: [] as Project[],
    paginatedProjects: [] as Project[],
    projectManagers: [] as User[],
    orderVendors: [] as OrderVendor[],
    orgSites: [] as OrgSite[],
    pendingBoqOrgSites: [] as OrgSite[],
    selectedOrgSiteId: '' as string,
    selectedOrgProjectId: '' as string,
    selectedDeliveryDate: '' as string,
    orderNotes: '' as string,
    orgAddressLine1: '',
    orgAddressCountry: '',
    orgAddressCountryCode: '',
    orgChanged: false as boolean,
    userProjectList: {} as OrganisationProjectListForUser,
    selectedRequests: new Set<SelectedRequest>(),
    paymentRequestIds: [] as string[],

    projectsPagination: {} as Pagination,

    projectParams: {
      pageSize: 10,
      orderByDesc: 'CreatedAt',
    } as ProjectParams,

    orgSiteParams: {
      boqStatus: 'Approved',
      pageSize: 1000,
    } as OrgSiteParams,

    templates: [] as Template[],

    paymentRequestVendors: [] as PRItem[],
    paymentRequestSites: [] as PRItem[],
    paymentRequestsCount: 0 as number,
    paymentRequests: [] as PaymentRequest[],
    paymentRequestsByVendor: [] as PaymentRequest[],
    paymentRequestsForLogPayment: [] as PaymentRequest[],
    paymentRequestsForLogPaymentContextOptions: [] as PaymentRequest[],
    paymentRequestTotals: {} as PaymentRequestTotals,
    paymentRequestTotalsByVendor: {} as PaymentRequestTotals,
    pushedBackCount: 0,
    PRPagination: {} as Pagination,
    PaymentMethodFilters: [
      { name: 'Cash on Delivery', id: 'COD' },
      { name: 'Account', id: 'Account' },
    ],

    PRParams: {
      pageSize: 20,
      orderByDesc: 'Date',
    } as AccountsParams,

    accountsPayable: [] as AccountPayable[],
    remainingAccountsPayable: [] as AccountPayable[],
    APPagination: {} as Pagination,
    lastPaymentRequestFilterType: '',

    APParams: {
      pageSize: 20,
      orderBy: 'NextDueDate',
    } as AccountsParams,
  }),

  actions: {
    async registerOrg(data: CreateOrgBody, firstCreate: boolean) {
      const toastStore = useToastStore();
      const authStore = useAuthStore();

      const response = await registerOrganisation(data);

      if (response.status === 200) {
        this.currentOrgDetails = response.data.data;
        await this.getCurrentUserOrgs();

        if (firstCreate) {
          for (const org of authStore.loggedInUser.organisations) org.userRole = OrgRoles.ORG_ADMIN;
          // Mirror the scalar seed in the array role model so hasOrgPermissions
          // (which now reads roles.organisations) sees ORG_ADMIN on the new org
          // immediately. The downstream getUserPermissions() reconciles with BE.
          authStore.seedOrgRoles(this.currentOrgDetails.id, [OrgRoles.ORG_ADMIN]);
          const createdOrg = authStore.loggedInUser.organisations.find(
            (org) => org.id === this.currentOrgDetails.id,
          );
          await authStore.setSelectedOrg(createdOrg as UserOrganisation);
        } else {
          toastStore.showToast(
            'success',
            t('organisationDetails.orgCreateSuccess.title'),
            t('organisationDetails.orgCreateSuccess.content'),
          );
        }
      }
    },

    async uploadDocuments(data: OrgDocsBody) {
      try {
        const response = await uploadDocuments(data);

        documentUploadToast({
          responseCode: response.status,
          title: t('organisationDetails.uploadDocSuccess.title'),
          description: t('organisationDetails.uploadDocSuccess.content'),
          onSuccess: async () => {
            this.currentOrgDetails = response.data.data;
          },
          onError: () => {
            console.error('Organisation document upload failed');
          },
        });
      } catch (error) {
        console.error('Error uploading organisation documents:', error);
        throw error;
      }
    },

    async deleteDocuments(data: DeleteDocsBody) {
      const toastStore = useToastStore();

      const response = await deleteDocuments(data);

      if (response.status === 204) {
        toastStore.showToast(
          'success',
          t('organisationDetails.deleteDocSuccess.title'),
          t('organisationDetails.deleteDocSuccess.content'),
        );
        this.currentOrgDetails = {
          ...this.currentOrgDetails,
          [data.key.includes('poa') ? 'proofOfAddressDocUrl' : 'vatDocUrl']: null,
        };
      }
    },

    async getOrgDetails() {
      const response = await getOrganisationDetails(currentOrgId());
      this.currentOrgDetails = response.data.data;
    },

    async getCurrentUserOrgs() {
      const authStore = useAuthStore();

      const response = await getCurrentUserOrgs();
      this.currentUserOrgs = response.data.data;
      authStore.setUserOrgs(this.currentUserOrgs);
    },

    // Deliberately separate from setSelectedOrg - pinning must not switch the active org.
    async setPrimaryOrganisation(id: string) {
      const authStore = useAuthStore();

      const response = await setOrganisationPrimary(id);
      this.currentUserOrgs = response.data.data;
      authStore.setUserOrgs(this.currentUserOrgs);
    },

    async getCurrentOrgUsers() {
      const version = this.orgUsersVersion;
      const response = await getCurrentOrgUsers(currentOrgId());

      // A role mutation landed while this fetch was in flight — its result is
      // authoritative, so drop this (now-stale) snapshot rather than clobber it.
      if (version !== this.orgUsersVersion) return;

      const users = response.data.data;

      const sortedUsers = sortRolesByOrder('org', users, {
        getRoleCode: (user: OrgUser) => user.roleCode,
        getPendingStatus: (user: OrgUser) => user.invitePending,
        userExistsOnPlatform: (user: OrgUser) => !!user.firstName,
      });

      this.currentOrgUsers = sortedUsers;
    },

    async getOrgProjectListByUser(userId: string) {
      const response = await getOrgProjectListByUser(currentOrgId(), userId);
      this.userProjectList = response.data.data;
    },

    async updateOrgUserRole(userId: string, role: UserRole) {
      const response = await updateUserRole(currentOrgId(), userId, role);
      this.currentOrgUsers = this.currentOrgUsers.map((user) =>
        user.userId === userId ? response.data.data : user,
      );
      this.orgUsersVersion += 1;

      analyticsService.capture('org_user_role_changed', {
        org_id: currentOrgId(),
        org_name: currentOrgName(),
        new_user_role: role,
      });

      useToastStore().showToast(
        'success',
        t('users.toasts.roleUpdate.title'),
        t('users.toasts.roleUpdate.content'),
      );
    },

    // When the user edits their OWN roles, mirror the server's updated role set
    // into the auth store so loggedInUser (which drives hasOrgPermissions/CASL and
    // is persisted) reflects the change immediately — otherwise their own roles
    // stay stale until a fresh login (a refresh re-hydrates the stale persisted user).
    syncSelfOrgRoles(userId: string, updatedUser: OrgUser) {
      const authStore = useAuthStore();
      if (userId !== authStore.loggedInUser?.id || !updatedUser?.roles) return;
      authStore.setOrgRoles(
        currentOrgId(),
        updatedUser.roles.map((role) => role.code),
      );
    },

    async addOrgUserRole(
      userId: string,
      roleCode: OrgRoles,
      email: string,
      options?: { skipAnalytics?: boolean; skipToast?: boolean },
    ) {
      const response = await addOrgUserRole(currentOrgId(), userId, roleCode);
      this.currentOrgUsers = this.currentOrgUsers.map((user) =>
        user.userId === userId ? response.data.data : user,
      );
      this.orgUsersVersion += 1;
      this.syncSelfOrgRoles(userId, response.data.data);

      // The implicit ORG_MEMB base-role add (when demoting a user to plain
      // member) passes skipAnalytics/skipToast — it is a side effect, not a user action.
      if (!options?.skipAnalytics) {
        analyticsService.capture('org_user_role_added', {
          org_id: currentOrgId(),
          org_name: currentOrgName(),
          role_code: roleCode,
        });
      }

      if (!options?.skipToast) {
        showRoleAddedToast(email, getRoleDisplayName(roleCode));
      }
    },

    async removeOrgUserRole(userId: string, roleCode: OrgRoles, email: string) {
      const response = await removeOrgUserRole(currentOrgId(), userId, roleCode);
      this.currentOrgUsers = this.currentOrgUsers.map((user) =>
        user.userId === userId ? response.data.data : user,
      );
      this.orgUsersVersion += 1;
      this.syncSelfOrgRoles(userId, response.data.data);

      analyticsService.capture('org_user_role_removed', {
        org_id: currentOrgId(),
        org_name: currentOrgName(),
        role_code: roleCode,
      });

      showRoleRemovedToast(email, getRoleDisplayName(roleCode));
    },

    async removeUser(userId: string, email: string, options?: { multiRole?: boolean }) {
      await deleteUser(currentOrgId(), userId);
      this.currentOrgUsers = this.currentOrgUsers.filter((user) => user.userId !== userId);
      this.orgUsersVersion += 1;

      analyticsService.capture('org_user_removed', {
        org_id: currentOrgId(),
        org_name: currentOrgName(),
        user_id: userId,
      });

      showUserRemovedToast('organisation', email, { multiRole: options?.multiRole });
    },

    async updateOrgDetails(data: Partial<Organisation>, reports?: boolean) {
      const authStore = useAuthStore();

      const response = await updateOrganisationDetails(currentOrgId(), data);
      this.currentOrgDetails = response.data.data;

      if (
        response.status === 200 &&
        authStore.loggedInUser?.selectedOrg &&
        response.data.data.name
      ) {
        const orgId = authStore.loggedInUser.selectedOrg.id;
        // Update in organisations array
        const orgInArray = authStore.loggedInUser.organisations?.find((org) => org.id === orgId);
        if (orgInArray) {
          orgInArray.name = response.data.data.name;
        }
        // Update selectedOrg
        authStore.loggedInUser.selectedOrg.name = response.data.data.name;
        localStorage.setItem('user', JSON.stringify(authStore.loggedInUser));
      }

      // Don't show update succes toast if updating details during account setup
      const isAccountSetup = computed(() => router.currentRoute.value.name === 'account-setup');

      if (!reports && !isAccountSetup.value) {
        useToastStore().showToast(
          'success',
          t('organisationDetails.updateSuccess.title'),
          t('organisationDetails.updateSuccess.content'),
        );
      }

      if (data.taxYearEnd) {
        this.currentOrgDetails.customData.taxYearEnd = data.taxYearEnd;
      }
      this.orgAddressLine1 = '';
    },

    async getOrgProjects(getAll: boolean, params: ProjectParams) {
      const response = getAll
        ? await getOrganisationProjects(selectedOrgId.value!, params)
        : await getOrganisationProjects(selectedOrgId.value!, { ...this.projectParams, ...params });

      if (getAll) {
        this.allProjects = response.data.data;
      } else {
        const { pageCount, totalCount, pageNo, pageSize } = response.data.meta.pagination;
        this.projectsPagination = { pageCount, totalCount, pageNo, pageSize };
        this.projectParams = { ...this.projectParams, ...params };

        this.paginatedProjects = response.data.data;
      }
    },

    updateProjectDataInList(updatedProject: Project) {
      const index = this.paginatedProjects.findIndex((p) => p.id === updatedProject.id);

      if (index !== -1) {
        this.paginatedProjects[index] = updatedProject;
      }
    },

    async getProjectManagers(id: string) {
      const response = await getProjectManagers(id);
      this.projectManagers = response.data.data;

      if (this.projectManagers) {
        this.projectManagers.forEach(
          (manager: User) => (manager.name = `${manager.firstName} ${manager.lastName}`),
        );
      }
    },

    async getBoqTemplates(id: string) {
      const response = await getBoqTemplates(id);
      this.templates = response.data.data;
    },

    async updateBoqTemplate(id: string, templateId: string, isPublic: boolean) {
      const response = await updateTemplate(id, templateId, isPublic);
      this.templates = this.templates.map((t) =>
        t.id === templateId
          ? {
              ...t,
              organisationId: isPublic ? null : response.data.data.organisationId,
            }
          : t,
      );
      useToastStore().showToast(
        'success',
        t('sites.boq.saveAsTemplateModal.updateTemplateAvailablity.title'),
        t(
          'sites.boq.saveAsTemplateModal.updateTemplateAvailablity.content' +
            (isPublic ? 'Public' : 'NotPublic'),
        ),
      );
    },

    setLastPaymentRequestFilterType(type: string) {
      this.lastPaymentRequestFilterType = type;
    },

    async getPaymentRequestVendors(id: string) {
      if (this.paymentRequestVendors.length === 0) {
        const response = await getPaymentRequestVendors(id);
        this.paymentRequestVendors = response.data.data;
        this.paymentRequestVendors = sortByStringField(this.paymentRequestVendors, 'name');
      }
    },

    async getPaymentRequestSites(id: string) {
      if (this.paymentRequestSites.length === 0) {
        const response = await getPaymentRequestSites(id);
        this.paymentRequestSites = response.data.data;
      }
    },

    async getPaymentRequestsCount(id: string) {
      const response = await getPaymentRequestsCount(id);
      this.paymentRequestsCount = response.data.data;
    },

    async getPaymentRequestTotals(params: { id: string; vendorId?: string }) {
      const response = await getPaymentRequestTotals(params);
      if (params.vendorId) {
        this.paymentRequestTotalsByVendor = response.data.data;
      } else {
        this.paymentRequestTotals = response.data.data;
      }
    },

    async getPaymentRequestCount(orgId: string, params?: AccountsParams) {
      const totalsStore = useTotalsStore();
      const {
        paymentRequestsByOrgCount,
        paymentRequestsByVendorCount,
        paymentRequestsBySiteCount,
      } = toRefs(totalsStore);

      const response = await getPaymentRequests(orgId, params);
      const totalCount = response.data.meta.pagination.totalCount;

      if (params?.vendorId) {
        paymentRequestsByVendorCount.value = totalCount;
      } else if (params?.siteId) {
        paymentRequestsBySiteCount.value = totalCount;
      } else {
        paymentRequestsByOrgCount.value = totalCount;
      }
    },

    /**
     * Fetches the count of pushed-back PRs for the given filters (PE-448).
     * Side-effect free: it requests a single row purely for the pagination
     * total and stores only `pushedBackCount` — it does NOT touch the PR list,
     * `PRParams`, or the tab totals (unlike `getPaymentRequestCount`).
     */
    async getPushedBackCount(orgId: string, params?: AccountsParams) {
      const read = ++pushedBackCountRead;
      const response = await getPaymentRequests(orgId, {
        ...params,
        isPushedBack: true,
        pageNo: 1,
        pageSize: 1,
      });
      if (read === pushedBackCountRead) {
        this.pushedBackCount = response.data.meta.pagination.totalCount;
      }
      return this.pushedBackCount;
    },

    async getPaymentRequests(orgId: string, params?: AccountsParams, forceRefresh = false) {
      const mergedParams = { ...this.PRParams, ...params };
      const paramsUnchanged = JSON.stringify(this.PRParams) === JSON.stringify(mergedParams);

      const isVendorRequest = mergedParams.vendorId !== null && mergedParams.vendorId !== undefined;

      const targetList = isVendorRequest ? this.paymentRequestsByVendor : this.paymentRequests;

      const hasCachedList = Array.isArray(targetList) && targetList.length > 0;

      if (!forceRefresh && hasCachedList && paramsUnchanged) {
        return targetList;
      }

      const read = ++paymentRequestsRead;
      const response = await getPaymentRequests(orgId, mergedParams);
      if (read !== paymentRequestsRead) {
        return isVendorRequest ? this.paymentRequestsByVendor : this.paymentRequests;
      }
      const data = response.data.data;

      const { pageCount, totalCount, pageNo, pageSize } = response.data.meta.pagination;

      this.PRPagination = { pageCount, totalCount, pageNo, pageSize };
      this.PRParams = mergedParams;
      this.paymentRequests = data;

      if (isVendorRequest) {
        this.paymentRequestsByVendor = response.data.data;
        return this.paymentRequestsByVendor;
      }

      return this.paymentRequests;
    },

    async getPaymentRequestsForLogPayment(orgId: string, params?: AccountsParams) {
      if (!params?.status) {
        const read = ++logPaymentContextOptionsRead;
        const response = await getPaymentRequests(orgId, { ...params, pageSize: 1000 });
        if (read === logPaymentContextOptionsRead) {
          this.paymentRequestsForLogPaymentContextOptions = response.data.data;
        }
      } else if (params.status === 'Approved') {
        const read = ++logPaymentRequestsRead;
        const response = await getPaymentRequests(orgId, { ...params, pageSize: 1000 });
        if (read === logPaymentRequestsRead) this.paymentRequestsForLogPayment = response.data.data;
      }
    },

    /** Drops every payment request list read in flight, so none can write back a snapshot taken before now. */
    invalidatePaymentRequestReads() {
      paymentRequestsRead++;
      logPaymentRequestsRead++;
      logPaymentContextOptionsRead++;
    },

    async getAccountsPayableTotals(
      params: {
        id: string;
        accountId?: string;
      },
      forceRefresh = false,
    ) {
      const { accountsPayableTotalsByOrg, accountsPayableTotalsByVendor } =
        toRefs(useTotalsStore());

      if (
        params.accountId &&
        Object.keys(accountsPayableTotalsByVendor.value ?? {}).length > 0 &&
        !forceRefresh
      ) {
        return;
      }

      if (
        !params.accountId &&
        Object.keys(accountsPayableTotalsByOrg.value ?? {}).length > 0 &&
        !forceRefresh
      ) {
        return;
      }

      const response = await getAccountsPayableTotals(params);

      if (params.accountId) {
        accountsPayableTotalsByVendor.value = response.data.data;
      } else {
        accountsPayableTotalsByOrg.value = response.data.data;
      }
    },

    async getAccountsPayable(orgId: string, params?: AccountsParams, forceRefresh = false) {
      const mergedParams = { ...this.APParams, ...params };

      const hasCachedData = Array.isArray(this.accountsPayable) && this.accountsPayable.length > 0;

      // Compare current params to merged params
      const paramsUnchanged = JSON.stringify(this.APParams) === JSON.stringify(mergedParams);

      if (hasCachedData && paramsUnchanged && !forceRefresh) {
        return;
      }

      const response = await getAccountsPayable(orgId, mergedParams);

      const { pageCount, totalCount, pageNo, pageSize } = response.data.meta.pagination;
      this.APPagination = { pageCount, totalCount, pageNo, pageSize };

      this.APParams = mergedParams;

      this.accountsPayable = response.data.data;
    },

    async getRemainingAccountsPayable(orgId: string) {
      this.remainingAccountsPayable = [];
      for (let i = this.APPagination.pageNo + 1; i <= this.APPagination.pageCount; i++) {
        try {
          const response = await getAccountsPayable(orgId, {
            pageNo: i,
            pageSize: this.APPagination.pageSize,
          });

          this.remainingAccountsPayable.push(...response.data.data);
        } catch (error) {
          console.error(error);
        }
      }
    },
    async getOrderVendors(id: string) {
      const response = await getOrderVendors(id);
      this.orderVendors = response.data.data;

      // Sorts list alphabetically by vendor name
      this.orderVendors = sortByStringField(this.orderVendors, 'vendorName');
    },

    async uploadOrgLogo(data: OrgDocsBody) {
      const toastStore = useToastStore();

      const response = await uploadOrgLogo(data);

      if (response.status === 200) {
        this.currentOrgDetails = response.data.data;
        toastStore.showToast(
          'success',
          t('organisationDetails.uploadLogoSuccess.title'),
          t('organisationDetails.uploadLogoSuccess.content'),
        );
      }
    },

    async deleteOrgLogo(id: string) {
      const toastStore = useToastStore();

      const response = await deleteOrgLogo(id);

      if (response.status === 204) {
        toastStore.showToast(
          'success',
          t('organisationDetails.deleteLogoSuccess.title'),
          t('organisationDetails.deleteLogoSuccess.content'),
        );

        this.currentOrgDetails.logoUrl = '';
        this.currentOrgDetails.logoName = '';
      }
    },

    // Returns the fetched sites in addition to the existing `orgSites` / `pendingBoqOrgSites`
    // side effect — callers juggling concurrent calls for the same field (e.g. a debounced
    // availability check) need their own result directly, not whichever call last wrote the
    // shared store field.
    async getOrgSites(id: string, params?: OrgSiteParams, itemSearch?: boolean) {
      const response = await getOrgSites(id, { ...this.orgSiteParams, ...params });

      if (params?.boqStatus === BOQStatus.pending) {
        this.pendingBoqOrgSites = response.data.data;
        return this.pendingBoqOrgSites;
      }

      this.orgSites = response.data.data;
      if (itemSearch && this.pendingBoqOrgSites.length > 0) {
        this.orgSites = this.orgSites.filter(
          (site: OrgSite) =>
            !this.pendingBoqOrgSites.some(
              (pendingBoqSite: OrgSite) => pendingBoqSite.id === site.id,
            ),
        );
      }
      return this.orgSites;
    },

    async createOrgOrders(id: string, data: CreateOrgOrder, siteId?: string) {
      const toastStore = useToastStore();
      const orderStore = useOrdersStore();
      const boqStore = useBoqStore();
      const response = await createOrgOrder(id, data);

      if (response.status === 200) {
        toastStore.showToast('success', t('orders.createOrder.toast.title'));

        if (siteId && boqStore.hasBoqForSite(siteId)) {
          await boqStore.getBOQ(siteId, true);
        }

        const createdOrders = response.data.data;

        for (const order of createdOrders) {
          analyticsService.capture('central_order_created', {
            org_id: selectedOrgId.value!,
            order_id: order.id,
            boq_id: order.boqId,
          });
        }

        orderStore.clearNewOrderCart();
        this.clearOrderDetails();
        orderStore.additions = [];

        return createdOrders;
      }

      return [];
    },

    async createOrgClaims(id: string, data: CreateOrgOrder, siteId?: string) {
      const toastStore = useToastStore();
      const orderStore = useOrdersStore();
      const claimStore = useClaimsStore();
      const boqStore = useBoqStore();
      const response = await createOrgClaim(id, data);

      if (response.status === 200) {
        const orders = response.data.data;
        toastStore.showToast('success', t('claims.createClaim.toast.title'));

        analyticsService.capture('central_claim_created', {
          org_id: selectedOrgId.value!,
          site_id: siteId ?? '',
        });

        for (const order of orders) {
          const paymentInfo = data.paymentInformation.find((p) => p.vendorId === order.vendorId);

          if (paymentInfo?.retentions && paymentInfo.retentions.length > 0) {
            const retentionData = paymentInfo.retentions.map((retention) => ({
              percent: retention.percent,
              dueDate: retention.dueDate,
              assigneeId: retention.assigneeId,
            }));

            await orderStore.addRetention(order.id, retentionData, order.version);
          }
        }

        for (const order of orders) {
          const itemContraChargeData = claimStore.itemContraCharges
            .filter((item) => item.newVendorId === order.vendorId)
            .map((item) => ({
              itemPath: item.path,
              itemInGroup: !!item.groupName,
              rate: item.defaultRate ?? 0,
              quantity: item.defaultQuantity ?? 1,
            }));

          if (itemContraChargeData.length > 0) {
            await orderStore.addItemContraCharge(
              order.id,
              itemContraChargeData,
              order.version,
              siteId,
            );
          }
        }

        for (const order of orders) {
          const valueContraChargeData = claimStore.valueContraCharges
            .filter((item) => item.vendorId === order.vendorId)
            .map((item) => ({ amount: item.amount }));

          if (valueContraChargeData.length > 0) {
            await orderStore.addValueContraCharge(order.id, valueContraChargeData, order.version);
          }
        }

        if (siteId && boqStore.hasBoqForSite(siteId)) {
          await boqStore.getBOQ(siteId, true);
        }

        orderStore.clearNewOrderCart();
        claimStore.retentions = [];
        claimStore.newRetentionCount = 1;
        claimStore.itemContraCharges = [];
        claimStore.valueContraCharges = [];
        claimStore.newValueContraChargeCount = 1;
        this.clearOrderDetails();

        return orders;
      }

      return [];
    },

    clearOrderDetails() {
      this.selectedOrgSiteId = '';
      this.selectedOrgProjectId = '';
      this.selectedDeliveryDate = '';
      this.orderNotes = '';
    },

    clearStore() {
      this.invalidatePaymentRequestReads();
      this.projectParams = {
        pageNo: 1,
        pageSize: 10,
        orderByDesc: 'CreatedAt',
      } as ProjectParams;
      this.orgSiteParams = {
        boqStatus: 'Approved',
        pageSize: 1000,
      };
      this.PRParams = {
        pageNo: 1,
        pageSize: 20,
        orderByDesc: 'Date',
      } as AccountsParams;
      this.APParams = {
        pageNo: 1,
        pageSize: 20,
        orderBy: 'NextDueDate',
      } as AccountsParams;

      this.currentOrgDetails = {} as Organisation;
      this.currentOrgUsers = [] as OrgUser[];
      this.orgUsersVersion = 0;
      this.currentUserOrgs = [] as UserOrganisation[];
      this.allProjects = [] as Project[];
      this.paginatedProjects = [] as Project[];
      this.projectManagers = [] as User[];
      this.orderVendors = [] as OrderVendor[];
      this.orgSites = [] as OrgSite[];
      this.pendingBoqOrgSites = [] as OrgSite[];
      this.selectedOrgSiteId = '' as string;
      this.selectedOrgProjectId = '' as string;
      this.selectedDeliveryDate = '' as string;
      this.orderNotes = '' as string;
      this.orgAddressLine1 = '';
      this.orgAddressCountry = '';
      this.orgAddressCountryCode = '';
      this.orgChanged = false as boolean;
      this.projectsPagination = {} as Pagination;
      this.templates = [] as Template[];
      this.paymentRequestVendors = [] as PRItem[];
      this.paymentRequestSites = [] as PRItem[];
      this.paymentRequestsCount = 0 as number;
      this.paymentRequests = [] as PaymentRequest[];
      this.paymentRequestsByVendor = [];
      this.paymentRequestsForLogPayment = [];
      this.paymentRequestsForLogPaymentContextOptions = [];
      this.paymentRequestTotals = {} as PaymentRequestTotals;
      this.paymentRequestTotalsByVendor = {} as PaymentRequestTotals;
      this.PRPagination = {} as Pagination;
      this.accountsPayable = [] as AccountPayable[];
      this.remainingAccountsPayable = [] as AccountPayable[];
      this.APPagination = {} as Pagination;
    },
  },
  getters: {
    paymentOptionsForSelect: (state) => {
      return state.PaymentMethodFilters.map((item) => ({
        name: item.name,
        id: item.id,
      }));
    },
  },
});
